// The service serves this bundle from its own origin in production, and Vite
// proxies the same paths in development, so relative URLs work in both.
const API = "/api";

/** The success envelope every endpoint returns. */
interface Envelope<T> {
  resCode: number;
  result: T;
}

/** The body returned for a failed request. */
interface ErrorBody {
  message: string;
}

/** Which link lifetimes the deployed storage backend can issue. */
export interface ServerConfig {
  /** Used when no ttl is sent, as a Go duration such as "10m0s". */
  defaultTtl: string;
  /** Longest finite lifetime available, or "" when there is no upper bound. */
  maxTtl: string;
  /** Whether a link with no expiry can be issued. */
  allowsNever: boolean;
  /** Image widths, in pixels, the server will accept. */
  sizes: number[];
  /** Used when no size is sent. */
  defaultSize: number;
}

export interface GenerateRequest {
  url: string;
  usage: string;
  /** A Go duration, "never", or omitted for the server default. */
  ttl?: string;
  /** One of the offered sizes, or omitted for the server default. */
  size?: number;
}

/**
 * A failure the server described well enough to show the user. The API returns
 * a helpful message for a 400 and a deliberately generic one for a 500, so the
 * message is safe to display either way.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${API}${path}`, init);
  } catch (cause) {
    throw new ApiError(0, "Could not reach the server. Check your connection and try again.");
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const message = isErrorBody(body) ? body.message : `Request failed (${response.status}).`;
    throw new ApiError(response.status, message);
  }

  if (!isEnvelope<T>(body)) {
    throw new ApiError(response.status, "The server sent a response this page did not understand.");
  }

  return body.result;
}

function isErrorBody(body: unknown): body is ErrorBody {
  return typeof body === "object" && body !== null && typeof (body as ErrorBody).message === "string";
}

function isEnvelope<T>(body: unknown): body is Envelope<T> {
  return typeof body === "object" && body !== null && "result" in body;
}

/** Reads the lifetimes this deployment supports. */
export function fetchConfig(): Promise<ServerConfig> {
  return request<ServerConfig>("/config");
}

/** Reads how many codes have been generated so far. */
export function fetchTotal(): Promise<number> {
  return request<{ totalNumber: number }>("/total").then((result) => result.totalNumber);
}

/** What a successful generation returns. */
export interface GenerateResult {
  /** Short link the stored PNG is served under. */
  imageUrl: string;
  /** Short link encoded in the QR code itself. */
  shortUrl: string;
  /** Where shortUrl redirects to. */
  targetUrl: string;
}

/** Generates a QR code and returns its links. */
export function generate(input: GenerateRequest): Promise<GenerateResult> {
  return request<GenerateResult>("/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}
